import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Eye, EyeOff, User, Mail, Phone, Lock, CheckCircle } from 'lucide-react';

const UserCredentialsModal = ({ 
  user, 
  open, 
  onClose, 
  onSave,
  title = "Edit Credentials"
}) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const content = {
    en: {
      title: "Edit User Credentials",
      name: "Full Name",
      email: "Email Address",
      password: "New Password",
      passwordHint: "Leave empty to keep current password",
      phone: "Phone Number",
      save: "Save Changes",
      cancel: "Cancel",
      success: "Credentials updated successfully!",
      role: "Role"
    },
    ar: {
      title: "تعديل بيانات المستخدم",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      password: "كلمة المرور الجديدة",
      passwordHint: "اتركها فارغة للإبقاء على كلمة المرور الحالية",
      phone: "رقم الهاتف",
      save: "حفظ التغييرات",
      cancel: "إلغاء",
      success: "تم تحديث البيانات بنجاح!",
      role: "الدور"
    }
  };

  const t = content[language];

  const roleLabels = {
    en: { admin: 'Admin', moderator: 'Moderator', teacher: 'Teacher', student: 'Student', affiliate: 'Affiliate' },
    ar: { admin: 'مدير', moderator: 'مشرف', teacher: 'معلم', student: 'طالب', affiliate: 'شريك' }
  };

  useEffect(() => {
    if (user && open) {
      setFormData({
        email: user.email || '',
        password: '',
        name: user.name || '',
        phone: user.phone || ''
      });
      setSuccess(false);
      setError('');
    }
  }, [user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Build update object (only include changed fields)
      const updates = {};
      if (formData.email !== user.email) updates.email = formData.email;
      if (formData.password) updates.password = formData.password;
      if (formData.name !== user.name) updates.name = formData.name;
      if (formData.phone !== (user.phone || '')) updates.phone = formData.phone || null;
      
      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setLoading(false);
        return;
      }
      
      await onSave(user.id, updates);
      setSuccess(true);
      
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-teal-600" />
            {title || t.title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {success && (
            <div className="bg-green-50 text-green-700 p-3 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {t.success}
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Role Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t.role}:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              user.role === 'teacher' ? 'bg-blue-100 text-blue-700' :
              user.role === 'student' ? 'bg-green-100 text-green-700' :
              user.role === 'moderator' ? 'bg-purple-100 text-purple-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {roleLabels[language][user.role] || user.role}
            </span>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              {t.name}
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              {t.email}
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-gray-400" />
              {t.password}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${language === 'ar' ? 'left-3' : 'right-3'}`}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500">{t.passwordHint}</p>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              {t.phone}
            </Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1234567890"
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserCredentialsModal;
